import numeral from 'numeral';
import BigNumber from 'bignumber.js';
import { Withdraw3Types } from '@nktkas/hyperliquid/api/exchange';
import { createL1ActionHash } from '@nktkas/hyperliquid/signing';
import { HLTokenInfo } from '@/lib/hl-api';
import { captureRequest } from '@/lib/analytics/posthog-events';
import { OrderBookLevel, ProcessedOrderBook } from './hl';

/** Privy useSignTypedData 返回的 signTypedData 函数类型 */
export type SignTypedDataFn = (typedData: any, options?: any) => Promise<{ signature: string }>;
export interface commonHlRequestParams {
  action: any;
  /** Privy signTypedData */
  signTypedData: SignTypedDataFn;
  t: any;
}

export interface SpreadItem {
  spread: string;
  nSigFigs: 2 | 3 | 4 | 5 | null;
  mantissa: 2 | 5 | null;
}

// Arbitrum USDC 合约地址
export const USDC_CONTRACT_ADDRESS = '0xaf88d065e77c8cC2239327C5EDb3A432268e5831';
// hyperliquid 充值收款地址
export const HYPERLIQUID_CONTRACT_ADDRESS = '0x2Df1c51E09aECF9cacB7bc98cB1742757f163dF7';
// 平台的反佣地址
export const PLATFORM_REBATE_ADDRESS = '0x5Bc104380EEade2cb9163c2146aC636863415e3f';

// 根据价格大小计算合适的小数位数
const getDecimalByPrice = (price: string): number => {
  const numPrice = new BigNumber(price);
  return numPrice.isGreaterThanOrEqualTo(10000)
    ? 0
    : numPrice.isGreaterThanOrEqualTo(1000)
      ? 1
      : numPrice.isGreaterThanOrEqualTo(100)
        ? 2
        : numPrice.isGreaterThanOrEqualTo(10)
          ? 3
          : numPrice.isGreaterThanOrEqualTo(1)
            ? 4
            : 5;
};

export const getTokenMeta = (_token: string, tokens: HLTokenInfo[]): HLTokenInfo | undefined => {
  if (!_token || !tokens.length) {
    return undefined;
  }
  const item = tokens.find((item: HLTokenInfo) => item.name.toLocaleLowerCase() === _token.toLocaleLowerCase());
  if (!item) {
    return undefined;
  }
  return {
    ...item,
    markPx: Number(item?.markPx || 0),
  };
};

export const extractPriceWithComma = ({
  price,
  decimal = 0,
  needSign = true,
}: {
  price: string | number;
  decimal?: number;
  needSign?: boolean;
  isRounding?: boolean;
}) => {
  const sign = Number(price) > 0 ? '+' : '-';
  const realPrice = String(price).startsWith('-') || String(price).startsWith('+') ? String(price).slice(1) : price;
  if (needSign) {
    return `${sign}$${formatPrice({ price: realPrice, decimal })}`;
  }
  return `$${formatPrice({ price: realPrice, decimal })}`;
};

// 将小数位数截断，并保留指定的小数位数，如果needZero，不足位数的补零，否则直接展示小数
// 如果小数过小，则默认保留非0部分两个小数
function truncateToFixed({
  value,
  decimals,
  padZero = false,
  defaultZero = '0',
  roundMode = BigNumber.ROUND_DOWN,
}: {
  value: string;
  decimals: number;
  padZero?: boolean;
  defaultZero?: string;
  roundMode?: BigNumber.RoundingMode;
}): string {
  const bn = new BigNumber(value || 0);
  if (bn.isZero()) return defaultZero;

  // 1️⃣ 按指定 decimals 截断
  let formatted = padZero
    ? bn.toFixed(decimals, roundMode) // ROUND_DOWN = 1，不四舍五入
    : bn.decimalPlaces(decimals, roundMode).toString();

  // 2️⃣ 防止科学计数法
  if (formatted.includes('e') || formatted.includes('E')) {
    formatted = new BigNumber(formatted).toFixed(decimals, 1);
  }

  // 3️⃣ 若结果为 0（说明小数太小被截断掉了）
  // if (new BigNumber(formatted).isZero()) {
  //   // 找到原始数的小数部分位置
  //   const parts = bn.toFixed().split('.');
  //   if (parts.length > 1) {
  //     const decimalsPart = parts[1];
  //     const firstNonZeroIndex = decimalsPart.search(/[1-9]/);

  //     // 若找到了非0位，则保留该位置 + 2 位
  //     if (firstNonZeroIndex !== -1) {
  //       const keepLen = firstNonZeroIndex + 3; // 非零位 + 后两位
  //       formatted = bn.toFixed(keepLen, 1);
  //     }
  //   }
  // }

  // 4️⃣ 若不补零，则去掉末尾多余的0
  if (!padZero) {
    formatted = formatted.replace(/(\.\d*?[1-9])0+$/, '$1').replace(/\.$/, '');
  }

  if (new BigNumber(formatted).isZero()) {
    return defaultZero;
  }
  return formatted;
}

// 非科学记数法格式化价格
export const formatPrice = ({
  price,
  decimal = 0,
  needZero = false,
  defaultZero = '0',
  roundMode = BigNumber.ROUND_DOWN,
}: {
  price: number | string;
  decimal?: number;
  needZero?: boolean;
  defaultZero?: string;
  roundMode?: BigNumber.RoundingMode;
}): string => {
  try {
    if (!price) {
      return '0';
    }
    const numPrice = typeof price === 'string' ? price : price.toString();
    // 根据价格大小动态调整小数位数
    const targetDecimal = decimal || getDecimalByPrice(numPrice);
    // 将小数位数截断，并保留指定的小数位数，不足的补零
    const realNum = truncateToFixed({
      value: numPrice,
      decimals: targetDecimal,
      padZero: needZero,
      defaultZero: defaultZero,
      roundMode,
    });
    // 使用千分位分隔符
    return numeral(realNum).format(`0,0.${'0'.repeat(targetDecimal)}`);
  } catch (error) {
    return '0';
  }
};

// 科学计数法格式化价格
export const formatPriceWithoutComma = ({
  price,
  decimal = 0,
  needZero = false,
  defaultZero = '0',
  roundMode = BigNumber.ROUND_DOWN,
  useLimit = false, // 是否需要进行limit判断
  limitAmount = 0.000001,
}: {
  price: number | string;
  decimal?: number;
  needZero?: boolean;
  defaultZero?: string;
  roundMode?: BigNumber.RoundingMode;
  useLimit?: boolean;
  limitAmount?: number;
}): string => {
  try {
    if (!price) {
      return defaultZero;
    }
    const numPrice = typeof price === 'string' ? price : price.toString();
    // 根据价格大小动态调整小数位数
    const targetDecimal = decimal || getDecimalByPrice(numPrice);

    if (new BigNumber(numPrice).isZero()) {
      // 如果是0，则返回执行的0默认值
      return defaultZero;
    } else if (useLimit && new BigNumber(numPrice).isLessThan(new BigNumber(limitAmount))) {
      // 如果小于limitAmount，则返回limitAmount
      return '<' + limitAmount.toString();
    } else {
      // 将小数位数截断，并保留指定的小数位数，不足的补零
      const realNum = truncateToFixed({
        value: numPrice,
        decimals: targetDecimal,
        padZero: needZero,
        defaultZero: defaultZero,
        roundMode,
      });
      return realNum;
    }
  } catch {
    return defaultZero;
  }
};

export const formatOrderbookPrice = (price: string) => {
  // 处理负数和小数
  const [integerPart, decimalPart] = price.toString().split('.');
  const formattedInteger = integerPart.replace(/(-?\d)(?=(\d{3})+$)/g, '$1,');
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

// 止盈止损计算百分比 - 下单
export const calculatePercent = ({
  point,
  price,
  leverage,
  isBuy,
  unit,
  amount,
}: {
  point: number | string; // 止盈价格
  price: number | string; // 当前价格
  leverage?: number | string; // 杠杆
  isBuy: boolean; // 是否是买单
  unit: '%' | '$'; // 单位，% 表示百分比，$ 表示美元
  amount: string; // 订单数量
}) => {
  if (unit === '%') {
    let val = isBuy ? ((Number(point) - Number(price)) * 100) / Number(price) : ((Number(price) - Number(point)) * 100) / Number(price);
    if (leverage) {
      val = val * Number(leverage);
    }
    return val.toFixed(2);
  } else {
    let val = new BigNumber(isBuy ? Number(point) - Number(price) : Number(price) - Number(point)).multipliedBy(new BigNumber(amount));
    return val.toFixed(2);
  }
};

export const calculateValueByPrice = ({
  start,
  end,
  size,
  unit,
  leverage,
}: {
  start: number | string;
  end: number | string;
  size: number | string;
  unit: '%' | '$';
  leverage?: number | string;
}) => {
  if (unit === '%') {
    let val: string | number = new BigNumber(end).minus(new BigNumber(start)).abs().toString();
    val = new BigNumber(val).dividedBy(new BigNumber(start)).toString();
    if (leverage) {
      val = new BigNumber(val).times(new BigNumber(leverage)).times(100).toString();
    }
    return new BigNumber(val).toFixed(2);
  } else {
    // 计算usdc盈利金额 - 保留两位小数
    let val: string | number = (Number(end) - Number(start)) * Number(size);
    return val.toFixed(2);
  }
};

export const calculateValueByPercent = ({
  price,
  percent,
  type,
  leverage,
  unit,
  amount,
}: {
  price: number | string;
  percent: number | string; // 百分比或者美元，取决于unit参数
  unit: '%' | '$';
  type: 'add' | 'sub';
  leverage?: number | string;
  amount: string;
}) => {
  let value = percent;
  if (leverage && unit === '%') {
    // 如果是$就不用处理杠杆
    value = new BigNumber(percent).dividedBy(new BigNumber(leverage)).toString();
  }
  if (type === 'add') {
    if (unit === '%') {
      // 按照百分比计算
      let val = (Number(price) * (100 + Number(value))) / 100;
      return formatPriceWithoutComma({ price: val });
    } else {
      // 正向计算： (止盈价格 - 当前价格) * 订单数量 * 杠杆 = 盈利
      // 反向计算：(盈利 / 订单数量) / 杠杆 + 当前价格
      let val = new BigNumber(value).dividedBy(new BigNumber(amount)).plus(new BigNumber(price)).toString();
      return formatPriceWithoutComma({ price: val });
    }
  } else {
    if (unit === '%') {
      // 按照百分比计算
      let val = (Number(price) * (100 - Number(value))) / 100;
      return formatPriceWithoutComma({ price: val });
    } else {
      // 正向计算： (止盈价格 - 当前价格) * 订单数量 * 杠杆 = 盈利
      // 反向计算：当前价格 - (盈利 / 订单数量) / 杠杆
      let val = new BigNumber(price).minus(new BigNumber(value).dividedBy(new BigNumber(amount))).toString();
      return formatPriceWithoutComma({ price: val });
    }
  }
};

export type UnitType = 'price' | 'percent';

export const capitalize = (str: string) => {
  if (typeof str !== 'string' || str.length === 0) return '';
  return str[0].toUpperCase() + str.slice(1).toLowerCase();
};

// long: 开仓做多
// short: 开仓做空
// closeShort: 平仓做空
// closeLong: 平仓做多
export const getDirection = (side: string, reduceOnly: boolean, t: any): string => {
  if (side === 'B') {
    return reduceOnly ? t('perps.closeShort') : t('perps.long');
  } else {
    return reduceOnly ? t('perps.closeLong') : t('perps.short');
  }
};

export const tokenSort = (tokens: HLTokenInfo[] | undefined, sortType: string, sortStates: 'asc' | 'desc'): HLTokenInfo[] => {
  if (!tokens) {
    return [];
  }
  if (sortType === 'default') {
    return tokens;
  } else if (sortType === 'symbol') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) => (sortStates === 'asc' ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name)));
  } else if (sortType === 'lastPrice') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) =>
      sortStates === 'asc' ? Number(a.markPx) - Number(b.markPx) : Number(b.markPx) - Number(a.markPx)
    );
  } else if (sortType === '24hChange') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) =>
      sortStates === 'asc' ? Number(a.change.percentage) - Number(b.change.percentage) : Number(b.change.percentage) - Number(a.change.percentage)
    );
  } else if (sortType === '8hVolume') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) =>
      sortStates === 'asc' ? Number(a.funding) - Number(b.funding) : Number(b.funding) - Number(a.funding)
    );
  } else if (sortType === 'dayNtlVlm') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) =>
      sortStates === 'asc' ? Number(a.dayNtlVlm) - Number(b.dayNtlVlm) : Number(b.dayNtlVlm) - Number(a.dayNtlVlm)
    );
  } else if (sortType === 'openInterest') {
    return tokens.sort((a: HLTokenInfo, b: HLTokenInfo) =>
      sortStates === 'asc' ? Number(a.realOpenInterest) - Number(b.realOpenInterest) : Number(b.realOpenInterest) - Number(a.realOpenInterest)
    );
  }
  return tokens;
};

export const getMinimumUnit = (numStr: number) => {
  // 清洗输入并验证格式
  const [, decimalPart] = numStr.toString().split('.');
  // 计算最小单位
  return decimalPart === '' ? 1 : Math.pow(10, -decimalPart.length);
};

// 价差规则
export const getSpreadList = (min: number, coin?: string, bestAsk?: number) => {
  if (bestAsk && bestAsk >= 100000) {
    // 如果超过10万，是另一个规则
    return [
      { spread: min.toString(), nSigFigs: null, mantissa: null } as SpreadItem,
      { spread: (min * 10).toString(), nSigFigs: 5, mantissa: null } as SpreadItem,
      { spread: (min * 10 * 2).toString(), nSigFigs: 5, mantissa: 2 } as SpreadItem,
      { spread: (min * 10 * 5).toString(), nSigFigs: 5, mantissa: 5 } as SpreadItem,
      { spread: (min * 100).toString(), nSigFigs: 4, mantissa: null } as SpreadItem,
      { spread: (min * 1000).toString(), nSigFigs: 3, mantissa: null } as SpreadItem,
      { spread: (min * 10000).toString(), nSigFigs: 2, mantissa: null } as SpreadItem,
    ];
  } else {
    const bn = new BigNumber(min);
    return [
      { spread: min.toString(), nSigFigs: null, mantissa: null } as SpreadItem,
      { spread: bn.times(2).toString(), nSigFigs: 5, mantissa: 2 } as SpreadItem,
      { spread: bn.times(5).toString(), nSigFigs: 5, mantissa: 5 } as SpreadItem,
      { spread: bn.times(10).toString(), nSigFigs: 4, mantissa: null } as SpreadItem,
      { spread: bn.times(100).toString(), nSigFigs: 3, mantissa: null } as SpreadItem,
      { spread: bn.times(1000).toString(), nSigFigs: 2, mantissa: null } as SpreadItem,
    ];
  }
};

// 计算24小时涨跌幅（已迁移到 @/lib/utils，此处 re-export 保持历史 import 兼容）
export { calculate24hChange } from '@/lib/utils';

export const calUsdValue = (price: number, leverage: number) => {
  return new BigNumber(price).times(leverage).toFixed(2);
};

/* ------------------------------ 以下是orderbook的计算逻辑 -------------------------------- */
export interface OrderBookData {
  coin: string;
  levels: Array<
    Array<{
      px: string;
      sz: string;
    }>
  >;
}

/**
 * 计算价差和最佳价格
 * @param orderbook 订单簿
 */
export const calculateSpreadInfo = (orderbook: ProcessedOrderBook): void => {
  if (orderbook.bids.length > 0 && orderbook.asks.length > 0) {
    orderbook.bestBid = Number(orderbook.bids[orderbook.bids.length - 1].px);
    orderbook.bestAsk = Number(orderbook.asks[0].px);
    orderbook.spread = Number(new BigNumber(orderbook.bestBid).minus(orderbook.bestAsk).toString());
    orderbook.spreadPercent = Number(((orderbook.spread / orderbook.bestBid) * 100).toFixed(4));
  }
};

/**
 * 计算累计量
 * @param levels 订单簿层级
 * @param direction 方向，up 表示降序，down 表示升序
 */
export const calculateCumulativeSize = (levels: OrderBookLevel[], direction: 'bid' | 'ask') => {
  if (direction === 'bid') {
    // 上 - 卖盘
    for (let i = levels.length - 1; i >= 0; i--) {
      levels[i].usdSize = Number(levels[i].price) * Number(levels[i].size);
      levels[i].usdTotal = levels[i].usdSize;
      levels[i].price = levels[i].price;
      levels[i].size = levels[i].size;
      levels[i].total = levels[i].size;
      if (i < levels.length - 1) {
        levels[i].total = Number(levels[i].total) + Number(levels[i + 1].total);
        levels[i].usdTotal = Number(levels[i].usdTotal) + Number(levels[i + 1].usdTotal);
      }
    }
    levels.map(level => {
      level.width = (level.total / levels[0].total) * 100;
    });
  } else if (direction === 'ask') {
    // 下 - 买盘
    for (let i = 0; i < levels.length; i++) {
      levels[i].usdSize = Number(levels[i].price) * Number(levels[i].size);
      levels[i].usdTotal = levels[i].usdSize;
      levels[i].price = levels[i].price;
      levels[i].size = levels[i].size; // 每行的原始值
      levels[i].total = levels[i].size; // 累计
      if (i > 0) {
        levels[i].total = Number(levels[i].total) + Number(levels[i - 1].total);
        levels[i].usdTotal = Number(levels[i].usdTotal) + Number(levels[i - 1].usdTotal);
      }
    }
    levels.map(level => {
      level.width = (level.total / levels[levels.length - 1].total) * 100;
    });
  }
};

export const calculateOrderBookData = (bookData: OrderBookData) => {
  const { levels } = bookData;
  const processedBook: ProcessedOrderBook = {
    bids: [],
    asks: [],
    bestBid: null,
    bestAsk: null,
    spread: null,
    spreadPercent: null,
  };

  if (!levels?.[0]?.length || !levels?.[1]?.length) {
    return processedBook;
  }
  if (levels && Array.isArray(levels)) {
    // 最佳卖价和最佳买价
    processedBook.bestAsk = Number(levels[0][0].px);
    processedBook.bestBid = Number(levels[1][0].px);
    // 处理订单簿层级
    const originAsks = levels[0].slice(0, 11); // 前11条
    const originBids = levels[1].slice(0, 11); // 前11条
    [originAsks, originBids].forEach((levels, index) => {
      levels.forEach((level: any) => {
        const { px, sz } = level;
        const levelData: any = {
          px,
          price: Number(px),
          size: Number(sz),
          total: 0,
          width: 0,
        };
        if (index === 0) {
          processedBook.asks.push(levelData);
        } else if (index === 1) {
          processedBook.bids.push(levelData);
        }
      });
    });
    // 降序排序
    processedBook.bids.sort((a: any, b: any) => b.price - a.price);
    processedBook.asks.sort((a: any, b: any) => b.price - a.price);
    // 计算累计量
    calculateCumulativeSize(processedBook.bids, 'bid');
    calculateCumulativeSize(processedBook.asks, 'ask');
    // 计算价差和最佳价格
    calculateSpreadInfo(processedBook);
  }
  return processedBook;
};

export const getPerpsLocalConf = (): {
  isUsdc: boolean;
  tpSlUnit: '%' | '$';
} | null => {
  const localConf = localStorage.getItem('billion-live-perps-config');
  if (localConf) {
    return JSON.parse(localConf);
  }
  return null;
};

/** Hyperliquid `withdraw3` 提交结果（签名 + POST /exchange） */
export type SubmitPerpsWithdraw3Result = { ok: true } | { ok: false; message: string; userRejected: boolean };

/**
 * 合约账户 USDC 提现到链上（withdraw3 + EIP-712 HyperliquidSignTransaction）。
 * 调用方需先校验 `destination`、`switchChain` 能力等；失败不抛异常，通过返回值的 `ok` 判断。
 */
const HL_CAPTURE_WITHDRAW3 = 'hl_exchange:withdraw3';

function captureReqMsg(msg: string): string {
  return msg.slice(0, 200);
}

export async function submitPerpsWithdraw3(params: {
  amount: string;
  destination: string;
  signTypedData: SignTypedDataFn;
  switchChain: (chainId: number) => Promise<void>;
}): Promise<SubmitPerpsWithdraw3Result> {
  const { amount, destination, signTypedData, switchChain } = params;

  try {
    await switchChain(42161);

    const nonce = Date.now();
    const signatureChainIdHex = '0xa4b1';
    const action = {
      type: 'withdraw3' as const,
      signatureChainId: signatureChainIdHex,
      hyperliquidChain: 'Mainnet' as const,
      destination: destination.toLowerCase(),
      amount,
      time: nonce,
    };

    const signResult = await signTypedData(
      {
        domain: {
          name: 'HyperliquidSignTransaction',
          version: '1',
          chainId: parseInt(action.signatureChainId),
          verifyingContract: '0x0000000000000000000000000000000000000000',
        },
        types: Withdraw3Types,
        primaryType: Object.keys(Withdraw3Types)[0],
        message: action,
      },
      { uiOptions: { showWalletUIs: false } }
    );

    const signatureString = signResult.signature;
    const signature = {
      r: `0x${signatureString.slice(2, 66)}` as `0x${string}`,
      s: `0x${signatureString.slice(66, 130)}` as `0x${string}`,
      v: parseInt(signatureString.slice(130, 132), 16) as 27 | 28,
    };

    const response = await fetch('https://api.hyperliquid.xyz/exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, signature, nonce }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      captureRequest({
        action: HL_CAPTURE_WITHDRAW3,
        status: 'fail',
        code: response.status,
        msg: captureReqMsg(errorText),
        type: 'hyperliquid',
      });
      return { ok: false, message: errorText, userRejected: false };
    }

    const result = await response.json();
    if (result.status === 'err') {
      const msg = typeof result.response === 'string' ? result.response : 'exchange_err';
      captureRequest({
        action: HL_CAPTURE_WITHDRAW3,
        status: 'fail',
        msg: captureReqMsg(msg),
        type: 'hyperliquid',
      });
      return { ok: false, message: msg, userRejected: false };
    }

    captureRequest({ action: HL_CAPTURE_WITHDRAW3, status: 'success', code: response.status, type: 'hyperliquid' });
    return { ok: true };
  } catch (error: unknown) {
    const err = error as { code?: number; message?: string };
    if (err.code === 4001 || err.message?.includes('User rejected')) {
      captureRequest({
        action: HL_CAPTURE_WITHDRAW3,
        status: 'fail',
        msg: 'user_rejected',
        type: 'hyperliquid',
        stage: 'signature',
      });
      return { ok: false, message: err.message || 'user_rejected', userRejected: true };
    }
    const fallback = err.message || String(error);
    captureRequest({
      action: HL_CAPTURE_WITHDRAW3,
      status: 'fail',
      msg: captureReqMsg(fallback),
      type: 'hyperliquid',
    });
    return { ok: false, message: fallback, userRejected: false };
  }
}

/**
 * 杠杆设置、设置TP/SL、取消委托订单
 * 失败时抛出异常，调用方负责 message 提示与 loading 状态
 */
export async function commonHlRequest(params: commonHlRequestParams): Promise<{
  status: 'ok' | 'err';
  response: string;
}> {
  const { action, signTypedData, t } = params;
  const actionType = typeof action?.type === 'string' ? action.type : 'unknown';
  const captureAction = `hl_exchange:l1:${actionType}`.slice(0, 300);
  const nonce = Date.now();

  const actionHash = createL1ActionHash({ action, nonce });
  const eip712Message = {
    source: 'a',
    connectionId: actionHash,
  };

  let signResult: { signature: string };
  try {
    signResult = await signTypedData(
      {
        domain: {
          name: 'Exchange',
          version: '1',
          chainId: 1337,
          verifyingContract: '0x0000000000000000000000000000000000000000',
        },
        types: {
          Agent: [
            { name: 'source', type: 'string' },
            { name: 'connectionId', type: 'bytes32' },
          ],
        },
        primaryType: 'Agent',
        message: eip712Message,
      },
      { uiOptions: { showWalletUIs: false } }
    );
  } catch (e: unknown) {
    const err = e as { code?: number; message?: string };
    const userRejected = err.code === 4001 || err.message?.includes('User rejected');
    captureRequest({
      action: captureAction,
      status: 'fail',
      msg: userRejected ? 'user_rejected' : captureReqMsg(err.message || String(e)),
      type: 'hyperliquid',
      stage: 'signature',
    });
    throw e;
  }

  const signatureString = signResult.signature;
  const signature = {
    r: `0x${signatureString.slice(2, 66)}` as `0x${string}`,
    s: `0x${signatureString.slice(66, 130)}` as `0x${string}`,
    v: parseInt(signatureString.slice(130, 132), 16) as 27 | 28,
  };

  const response = await fetch('https://api.hyperliquid.xyz/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action, signature, nonce }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    captureRequest({
      action: captureAction,
      status: 'fail',
      code: response.status,
      msg: captureReqMsg(errorText),
      type: 'hyperliquid',
    });
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = await response.json();
  if (result.status === 'err') {
    if (result.response.startsWith('Position does not have sufficient margin for reduction.')) {
      captureRequest({
        action: captureAction,
        status: 'fail',
        msg: captureReqMsg(String(result.response)),
        type: 'hyperliquid',
      });
      return {
        status: 'err',
        response: t('perps.positionNotEnoughMargin'),
      };
    } else if (result.response.startsWith('Account does not have sufficient margin available for increase.')) {
      captureRequest({
        action: captureAction,
        status: 'fail',
        msg: captureReqMsg(String(result.response)),
        type: 'hyperliquid',
      });
      return {
        status: 'err',
        response: t('perps.accountNotEnoughMargin'),
      };
    }
    captureRequest({
      action: captureAction,
      status: 'fail',
      msg: captureReqMsg(String(result.response)),
      type: 'hyperliquid',
    });
    return {
      status: 'err',
      response: result.response,
    };
  }

  if (result?.response?.data?.statuses?.[0]?.error) {
    const orderErr = String(result?.response?.data?.statuses?.[0]?.error || '取消订单失败');
    captureRequest({
      action: captureAction,
      status: 'fail',
      msg: captureReqMsg(orderErr),
      type: 'hyperliquid',
    });
    throw new Error(orderErr);
  }

  captureRequest({ action: captureAction, status: 'success', code: response.status, type: 'hyperliquid' });
  return {
    status: 'ok',
    response: '',
  };
}
